import styled from "styled-components";
import { useEffect, useState, useRef } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { captureException } from "@sentry/react";
import { isURL } from "validator";
import { addToStateArray, removeFromStateArray } from "modules/functions";

import LoadingBox from "components/other/LoadingBox";
import BoxLoadingSpinner from "components/other/BoxLoadingSpinner";
import Backdrop from "components/other/Backdrop";
import MessageWindow from "components/other/MessageWindow";
import SponsoredMessages from "./SponsoredMessages";
import { AD_PLAN_EXTENDED_POST } from "constants";

const ModalWrap = styled.div`
  width: 100%;
  margin: 0;
  padding: 0;
`;

export default ({ profileData }) => {
  const [messageForm, setMessageForm] = useState(false);
  const [messageInputs, setMessageInputs] = useState({
    text: "",
    linkText: "",
    link: "",
  });
  const [warnings, setWarnings] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [switchLoading, setSwitchLoading] = useState([]);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [editMessageModal, setEditMessageModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // Fetch data
  const GET_SPONSORED_MESSAGES_DATA = gql`
    query GetSponsoredMessagesData {
      sponsoredMessages {
        id
        status
        text
        link_text
        link
        switched_on
        rejection_reason
        sponsored_messages_stats {
          id
          to
          total_impressions
          total_clicks
        }
        impressions {
          id
          created_at
          clicked
        }
      }
    }
  `;

  const { data, error, refetch } = useQuery(GET_SPONSORED_MESSAGES_DATA, {
    fetchPolicy: "network-only",
  });

  if (error) {
    console.log("Fetching sponsored messages data error", error);
    captureException(error);
  }

  // Handle message form
  const toggleMessageForm = () => {
    setErrorMessage(null);

    if (profileData.adPlan < AD_PLAN_EXTENDED_POST) {
      console.log("Does not have required ad plan");
      setErrorMessage(
        "Sponsored messages can only be created by users with a Gold advertising plan."
      );
      return;
    }

    setMessageForm((prevState) => !prevState);
  };

  const textMaxLength = 90;
  const linkTextMaxLength = 30;

  const handleInputChange = (e) => {
    if (submitLoading) {
      return;
    }

    const { target } = e;

    let newInputs = { ...messageInputs };

    newInputs[target.name] = target.value;

    setMessageInputs(newInputs);
  };

  // Submit message
  const SUBMIT_SPONSORED_MESSAGE = gql`
    mutation SubmitSponsoredMessage(
      $text: String!
      $linkText: String!
      $link: String!
    ) {
      submitSponsoredMessage(text: $text, linkText: $linkText, link: $link) {
        resultCode
      }
    }
  `;

  const [submitSponsoredMessage] = useMutation(SUBMIT_SPONSORED_MESSAGE);

  const onMessageSubmit = async () => {
    try {
      setSubmitError(null);

      if (submitLoading) return;

      let newWarnings = [];

      const { text, linkText, link } = messageInputs;

      if (text.trim().length < 5) newWarnings.push("text");
      if (linkText.trim().length < 3) newWarnings.push("linkText");
      if (link.trim().length < 8) newWarnings.push("link");
      if (
        !isURL(link.trim(), {
          protocols: ["http", "https"],
          require_valid_protocol: true,
          require_protocol: true,
        })
      )
        newWarnings.push("link");

      if (newWarnings.length !== 0) {
        setWarnings(newWarnings);
        return;
      }

      setWarnings([]);
      setSubmitLoading(true);

      const result = await submitSponsoredMessage({
        variables: {
          text: text.trim(),
          linkText: linkText.trim(),
          link: link.trim(),
        },
      });

      setSubmitLoading(false);

      console.log("result", result);

      if (result.data.submitSponsoredMessage.resultCode === 6) {
        setTimeout(
          () =>
            setSubmitError(
              "You can create a maximum of 20 sponsored messages. To create a new message, please delete an older one first."
            ),
          300
        );

        return;
      }

      if (result.data.submitSponsoredMessage.resultCode !== 0) {
        setTimeout(
          () =>
            setSubmitError(
              "Failed to create the message. Please try again later or contact support."
            ),
          300
        );

        return;
      }

      setMessageInputs({
        text: "",
        linkText: "",
        link: "",
      });
      setTimeout(() => setMessageForm(false), 500);
      refetch();
    } catch (error) {
      captureException(error);
      console.log("Submit message error", error);
      setSubmitLoading(false);
      setSubmitError(
        "Failed to create the message. Please try again later or contact support."
      );
    }
  };

  // handle Message switch
  const TOGGLE_SPONSORED_MESSAGE_SWITCH = gql`
    mutation ToggleSponsoredMessageSwitch($messageId: ID!) {
      toggleSponsoredMessageSwitch(messageId: $messageId) {
        id
        status
        switched_on
      }
    }
  `;

  const [toggleSponsoredMessageSwitch] = useMutation(
    TOGGLE_SPONSORED_MESSAGE_SWITCH
  );

  const onSwitchClick = async (messageId) => {
    try {
      if (!messageId) return;

      setErrorMessage(null);

      if (profileData.adPlan < AD_PLAN_EXTENDED_POST) {
        console.log("Does not have required ad plan");
        setErrorMessage(
          "Sponsored messages can only be created by users with a Gold advertising plan."
        );
        return;
      }

      if (switchLoading.includes(messageId)) return;

      addToStateArray(switchLoading, messageId, setSwitchLoading);

      await toggleSponsoredMessageSwitch({
        variables: {
          messageId,
        },
      });

      removeFromStateArray(switchLoading, messageId, setSwitchLoading);

      refetch();
    } catch (error) {
      console.log("error", error);
      captureException(error);
      return;
    }
  };

  // handle Message delete
  const onDeleteClick = (messageId) => {
    setMessageToDelete(messageId);
  };

  const closeDeleteModal = () => {
    setMessageToDelete(null);
  };

  const DELETE_MESSAGE = gql`
    mutation DeleteMessage($messageId: ID!) {
      deleteSponsoredMessage(messageId: $messageId) {
        resultCode
      }
    }
  `;

  const [deleteSponsoredMessage] = useMutation(DELETE_MESSAGE);

  const onConfirmDelete = async () => {
    try {
      if (!messageToDelete) return;

      if (loadingDelete) return;

      setLoadingDelete(true);

      await deleteSponsoredMessage({
        variables: {
          messageId: messageToDelete,
        },
      });

      refetch();

      setLoadingDelete(false);
      setMessageToDelete(null);
    } catch (error) {
      console.log("Confirm delete error", error);
      captureException(error);
      setLoadingDelete(false);
      return;
    }
  };

  // handle Message edit info
  const onEditClick = () => {
    setEditMessageModal(true);
  };

  const closeEditMessageModal = () => {
    setEditMessageModal(false);
  };

  //console.log("data", data);

  return (
    <>
      {!data && (
        <LoadingBox>
          <BoxLoadingSpinner height="230px" />
        </LoadingBox>
      )}
      {data && (
        <SponsoredMessages
          data={data}
          messageInputs={messageInputs}
          warnings={warnings}
          handleInputChange={handleInputChange}
          profileData={profileData}
          textMaxLength={textMaxLength}
          linkTextMaxLength={linkTextMaxLength}
          submitLoading={submitLoading}
          onMessageSubmit={onMessageSubmit}
          submitError={submitError}
          messageForm={messageForm}
          toggleMessageForm={toggleMessageForm}
          switchLoading={switchLoading}
          onSwitchClick={onSwitchClick}
          messageToDelete={messageToDelete}
          onDeleteClick={onDeleteClick}
          closeDeleteModal={closeDeleteModal}
          onConfirmDelete={onConfirmDelete}
          editMessageModal={editMessageModal}
          onEditClick={onEditClick}
          closeEditMessageModal={closeEditMessageModal}
          errorMessage={errorMessage}
        />
      )}
    </>
  );
};
